import { isSensitiveQueryEntry } from "@/lib/privacy/urlSanitization";

export const BOOKING_TERMS_DOCUMENT_ID = "singing-attitude-uk-terms" as const;
// Factual local-offer alignment. Historical acceptances retain their original edition.
// This release does not imply a new legal/privacy/media approval or activate booking automation.
export const BOOKING_TERMS_VERSION = "UK-TERMS-LOCAL-2026-09-07" as const;
export const SUPPORTED_BOOKING_TERMS_VERSIONS = ["UK-TERMS-ELY-2026-08-10", BOOKING_TERMS_VERSION] as const;
export const BOOKING_TERMS_EFFECTIVE_DATE = "7 September 2026" as const;
export const BOOKING_PRIVACY_DOCUMENT_ID = "singing-attitude-uk-privacy" as const;
export const BOOKING_PRIVACY_VERSION = "DRAFT-UK-PRIVACY-2026-07-14" as const;
export const BOOKING_PRIVACY_EFFECTIVE_DATE = "Pending approval" as const;
// Draft identifier: the checkout summary and policy coverage require legal/business approval before deployment.
export const MEDIA_CONSENT_DOCUMENT_ID = "singing-attitude-uk-media-consent-summary" as const;
export const MEDIA_CONSENT_VERSION = "DRAFT-UK-MEDIA-2026-07-13" as const;
export const ELY_EARLY_PERFORMANCE_DOCUMENT_ID =
  "singing-attitude-ely-early-performance-request" as const;
export const ELY_EARLY_PERFORMANCE_VERSION =
  "ELY-EARLY-PERFORMANCE-REQUEST-2026-08-09" as const;
export const ELY_ANALYTICS_CONSENT_EVIDENCE_SOURCE =
  "browser_consent_state_v1" as const;

export const MEDIA_CONSENT_PURPOSES = [
  "website_portal",
  "organic_social",
  "paid_advertising",
  "educational_training",
  "first_name",
  "testimonial_comments",
] as const;

export type MediaConsentPurpose = (typeof MEDIA_CONSENT_PURPOSES)[number];
export type MediaSubjectAgeBand = "adult" | "under_18";

export const MEDIA_CONSENT_PURPOSE_LABELS: Record<MediaConsentPurpose, string> = {
  website_portal: "Website and Singing Attitude portal",
  organic_social: "Organic social-media content",
  paid_advertising: "Paid digital advertising",
  educational_training: "Educational and training content",
  first_name: "Use of my first name alongside the content",
  testimonial_comments: "Use of my testimonial or spoken comments",
};

export type CheckoutConsentInput = {
  termsAccepted: boolean;
  earlyPerformanceRequested: boolean;
  earlyPerformanceAcknowledged: boolean;
  mediaOptIn: boolean;
  mediaPurposes: MediaConsentPurpose[];
  reviewBeforeFirstPublication: boolean;
  mediaSubjectAgeBand: MediaSubjectAgeBand | null;
};

export const EMPTY_CHECKOUT_CONSENT: CheckoutConsentInput = {
  termsAccepted: false,
  earlyPerformanceRequested: false,
  earlyPerformanceAcknowledged: false,
  mediaOptIn: false,
  mediaPurposes: [],
  reviewBeforeFirstPublication: false,
  mediaSubjectAgeBand: null,
};

export type ElyAnalyticsConsentInput = {
  analytics: "granted" | "denied";
  version?: string;
  updatedAt?: string;
  attribution?: {
    first_touch?: Record<string, unknown>;
    latest_touch?: Record<string, unknown>;
  };
};

export type ElyAnalyticsConsentEnvelope = {
  status: "granted" | "denied" | "unknown";
  version: string | null;
  capturedAt: string | null;
  evidenceSource: typeof ELY_ANALYTICS_CONSENT_EVIDENCE_SOURCE;
  attribution: Record<string, string>;
};

export type BookingConsentEnvelope = {
  terms: {
    accepted: true;
    documentId: typeof BOOKING_TERMS_DOCUMENT_ID;
    version: (typeof SUPPORTED_BOOKING_TERMS_VERSIONS)[number];
    privacyDocumentId: typeof BOOKING_PRIVACY_DOCUMENT_ID;
    privacyVersion: typeof BOOKING_PRIVACY_VERSION;
  };
  media: {
    status: "granted" | "partially_granted" | "declined";
    purposes: MediaConsentPurpose[];
    documentId: typeof MEDIA_CONSENT_DOCUMENT_ID;
    version: typeof MEDIA_CONSENT_VERSION;
    reviewBeforeFirstPublication: boolean;
    guardianStatus: "not_applicable" | "required";
    sessionConfirmationRequired: true;
  };
  earlyPerformance: {
    requested: boolean;
    acknowledged: boolean;
    documentId: typeof ELY_EARLY_PERFORMANCE_DOCUMENT_ID;
    version: typeof ELY_EARLY_PERFORMANCE_VERSION;
  };
  analytics: ElyAnalyticsConsentEnvelope;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function isMediaPurpose(value: unknown): value is MediaConsentPurpose {
  return typeof value === "string" && (MEDIA_CONSENT_PURPOSES as readonly string[]).includes(value);
}

function normalizeAnalyticsAttribution(value: unknown) {
  if (!isRecord(value)) return {};
  const output: Record<string, string> = {};
  const allowed = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"] as const;
  for (const [prefix, touch] of [
    ["first", value.first_touch],
    ["latest", value.latest_touch],
  ] as const) {
    if (!isRecord(touch)) continue;
    for (const key of allowed) {
      const candidate = touch[key];
      const normalized = typeof candidate === "string" ? candidate.trim().slice(0, 160) : "";
      const containsPersonalIdentifier = (
        /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i.test(normalized) ||
        /\b[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}\b/i.test(normalized) ||
        /\+?[0-9][0-9 ()-]{7,}[0-9]/.test(normalized)
      );
      if (normalized && !containsPersonalIdentifier && !isSensitiveQueryEntry(key, normalized)) {
        output[`${prefix}_${key}`] = normalized;
      }
    }
  }
  return output;
}

const ANALYTICS_ATTRIBUTION_KEYS = new Set([
  "first_utm_source", "first_utm_medium", "first_utm_campaign", "first_utm_term", "first_utm_content",
  "latest_utm_source", "latest_utm_medium", "latest_utm_campaign", "latest_utm_term", "latest_utm_content",
]);

function isAnalyticsAttribution(value: unknown) {
  return isRecord(value) && Object.entries(value).every(([key, candidate]) => (
    ANALYTICS_ATTRIBUTION_KEYS.has(key) && typeof candidate === "string" &&
    candidate.length > 0 && candidate.length <= 160 &&
    !isSensitiveQueryEntry(key, candidate) &&
    !/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i.test(candidate) &&
    !/\b[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}\b/i.test(candidate) &&
    !/\+?[0-9][0-9 ()-]{7,}[0-9]/.test(candidate)
  ));
}

export function validateElyAnalyticsConsentInput(value: unknown): ElyAnalyticsConsentEnvelope {
  if (!isRecord(value)) {
    return {
      status: "unknown",
      version: null,
      capturedAt: null,
      evidenceSource: ELY_ANALYTICS_CONSENT_EVIDENCE_SOURCE,
      attribution: {},
    };
  }
  const allowed = new Set(["analytics", "version", "updatedAt", "attribution"]);
  if (Object.keys(value).some((key) => !allowed.has(key))) {
    throw new Error("Analytics consent details contain an unexpected field.");
  }
  if (value.analytics !== "granted" && value.analytics !== "denied") {
    throw new Error("Analytics consent status is invalid.");
  }
  const version = typeof value.version === "string" && /^[A-Za-z0-9_.:-]{1,80}$/.test(value.version)
    ? value.version
    : null;
  const capturedAt = typeof value.updatedAt === "string" && Number.isFinite(Date.parse(value.updatedAt))
    ? new Date(value.updatedAt).toISOString()
    : null;
  const status = version && capturedAt ? value.analytics : "unknown";
  return {
    status,
    version,
    capturedAt,
    evidenceSource: ELY_ANALYTICS_CONSENT_EVIDENCE_SOURCE,
    attribution: status === "granted" ? normalizeAnalyticsAttribution(value.attribution) : {},
  };
}

export function validateCheckoutConsentInput(value: unknown): CheckoutConsentInput {
  if (!isRecord(value)) throw new Error("Consent details are required.");
  const allowed = new Set([
    "termsAccepted",
    "earlyPerformanceRequested",
    "earlyPerformanceAcknowledged",
    "mediaOptIn",
    "mediaPurposes",
    "reviewBeforeFirstPublication",
    "mediaSubjectAgeBand",
  ]);
  if (Object.keys(value).some((key) => !allowed.has(key))) {
    throw new Error("Consent details contain an unexpected field.");
  }
  if (value.termsAccepted !== true) {
    throw new Error("Please accept the Terms and Conditions to continue.");
  }
  if (typeof value.mediaOptIn !== "boolean") throw new Error("Media preference is invalid.");
  if (!Array.isArray(value.mediaPurposes) || value.mediaPurposes.some((purpose) => !isMediaPurpose(purpose))) {
    throw new Error("Media purposes are invalid.");
  }
  const purposes = Array.from(new Set(value.mediaPurposes as MediaConsentPurpose[]));
  if (typeof value.reviewBeforeFirstPublication !== "boolean") {
    throw new Error("Publication-review preference is invalid.");
  }
  const ageBand = value.mediaSubjectAgeBand;
  if (ageBand !== null && ageBand !== "adult" && ageBand !== "under_18") {
    throw new Error("Media subject age band is invalid.");
  }
  if (value.mediaOptIn && ageBand === null) {
    throw new Error("Please confirm whether the person who may be recorded is 18 or over.");
  }
  if (value.mediaOptIn && ageBand === "adult" && purposes.length === 0) {
    throw new Error("Select at least one media purpose or clear the optional media preference.");
  }
  return {
    termsAccepted: true,
    earlyPerformanceRequested: value.earlyPerformanceRequested === true,
    earlyPerformanceAcknowledged: value.earlyPerformanceAcknowledged === true,
    mediaOptIn: value.mediaOptIn,
    mediaPurposes: value.mediaOptIn && ageBand === "adult" ? purposes : [],
    reviewBeforeFirstPublication:
      value.mediaOptIn && ageBand === "adult" && purposes.length > 0
        ? value.reviewBeforeFirstPublication
        : false,
    mediaSubjectAgeBand: value.mediaOptIn ? ageBand : null,
  };
}

export function requireElyEarlyPerformanceRequest(value: CheckoutConsentInput) {
  if (!value.earlyPerformanceRequested || !value.earlyPerformanceAcknowledged) {
    throw new Error(
      "Please make the separate early-performance request and acknowledgement for an Ely lesson.",
    );
  }
}

export function buildBookingConsentEnvelope(
  value: unknown,
  analyticsConsent?: unknown,
): BookingConsentEnvelope {
  const input = validateCheckoutConsentInput(value);
  const guardianRequired = input.mediaOptIn && input.mediaSubjectAgeBand === "under_18";
  const status = !input.mediaOptIn || guardianRequired
    ? "declined"
    : input.mediaPurposes.length === MEDIA_CONSENT_PURPOSES.length
      ? "granted"
      : "partially_granted";
  return {
    terms: {
      accepted: true,
      documentId: BOOKING_TERMS_DOCUMENT_ID,
      version: BOOKING_TERMS_VERSION,
      privacyDocumentId: BOOKING_PRIVACY_DOCUMENT_ID,
      privacyVersion: BOOKING_PRIVACY_VERSION,
    },
    media: {
      status,
      purposes: guardianRequired ? [] : input.mediaPurposes,
      documentId: MEDIA_CONSENT_DOCUMENT_ID,
      version: MEDIA_CONSENT_VERSION,
      reviewBeforeFirstPublication: guardianRequired ? false : input.reviewBeforeFirstPublication,
      guardianStatus: guardianRequired ? "required" : "not_applicable",
      sessionConfirmationRequired: true,
    },
    earlyPerformance: {
      requested: input.earlyPerformanceRequested,
      acknowledged: input.earlyPerformanceAcknowledged,
      documentId: ELY_EARLY_PERFORMANCE_DOCUMENT_ID,
      version: ELY_EARLY_PERFORMANCE_VERSION,
    },
    analytics: validateElyAnalyticsConsentInput(analyticsConsent),
  };
}

export function validateBookingConsentEnvelope(value: unknown): BookingConsentEnvelope {
  if (
    !isRecord(value) || !isRecord(value.terms) || !isRecord(value.media) ||
    !isRecord(value.earlyPerformance) || !isRecord(value.analytics)
  ) {
    throw new Error("Consent audit envelope is invalid.");
  }
  const analyticsAttribution = value.analytics.attribution;
  if (
    value.terms.accepted !== true ||
    value.terms.documentId !== BOOKING_TERMS_DOCUMENT_ID ||
    !(SUPPORTED_BOOKING_TERMS_VERSIONS as readonly unknown[]).includes(value.terms.version) ||
    value.terms.privacyDocumentId !== BOOKING_PRIVACY_DOCUMENT_ID ||
    value.terms.privacyVersion !== BOOKING_PRIVACY_VERSION
  ) {
    throw new Error("Terms acceptance version is invalid.");
  }
  if (
    value.media.documentId !== MEDIA_CONSENT_DOCUMENT_ID ||
    value.media.version !== MEDIA_CONSENT_VERSION ||
    !["granted", "partially_granted", "declined"].includes(String(value.media.status)) ||
    !Array.isArray(value.media.purposes) ||
    value.media.purposes.some((purpose) => !isMediaPurpose(purpose)) ||
    typeof value.media.reviewBeforeFirstPublication !== "boolean" ||
    !["not_applicable", "required"].includes(String(value.media.guardianStatus)) ||
    value.media.sessionConfirmationRequired !== true
  ) {
    throw new Error("Media preference version is invalid.");
  }
  if (
    typeof value.earlyPerformance.requested !== "boolean" ||
    typeof value.earlyPerformance.acknowledged !== "boolean" ||
    value.earlyPerformance.documentId !== ELY_EARLY_PERFORMANCE_DOCUMENT_ID ||
    value.earlyPerformance.version !== ELY_EARLY_PERFORMANCE_VERSION
  ) {
    throw new Error("Early-performance request version is invalid.");
  }
  if (
    !["granted", "denied", "unknown"].includes(String(value.analytics.status)) ||
    value.analytics.evidenceSource !== ELY_ANALYTICS_CONSENT_EVIDENCE_SOURCE ||
    !isAnalyticsAttribution(analyticsAttribution) ||
    (value.analytics.status === "granted" && (
      typeof value.analytics.version !== "string" ||
      !/^[A-Za-z0-9_.:-]{1,80}$/.test(value.analytics.version) ||
      typeof value.analytics.capturedAt !== "string" ||
      !Number.isFinite(Date.parse(value.analytics.capturedAt))
    )) ||
    (value.analytics.status !== "granted" &&
      isRecord(analyticsAttribution) && Object.keys(analyticsAttribution).length > 0)
  ) {
    throw new Error("Analytics consent evidence is invalid.");
  }
  return value as BookingConsentEnvelope;
}
